import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProject, forkProject, listProjectForks } from '../../api/projects';
import { ComparisonBarChart } from '../charts/ComparisonBarChart';
import { fmt } from '../../utils/format';
import { toast } from '../../stores/uiStore';
import type { GrandProjet } from '../../types/estimation';
import styles from './ScenarioComparisonPage.module.css';

const SCENARIO_COLORS = ['#6366f1', '#22d3ee', '#f39c12', '#e74c3c', '#9b59b6'];

interface MetricDef {
  key: keyof GrandProjet;
  label: string;
  unit?: string;
  group: string;
}

const METRICS: MetricDef[] = [
  { key: 'logements', label: 'Logements', group: 'Programme' },
  { key: 'emplois', label: 'Emplois', group: 'Programme' },
  { key: 'ha_total', label: 'Total Area', unit: 'ha', group: 'Programme' },
  { key: 'ha_industriel', label: 'Industriel (ZI)', unit: 'ha', group: 'Programme' },
  { key: 'horizon_years', label: 'Horizon', unit: 'years', group: 'Programme' },
  { key: 'sbp_logements', label: 'SBP Logements', unit: 'm\u00B2', group: 'SBP' },
  { key: 'sbp_activites', label: 'SBP Activit\u00E9s', unit: 'm\u00B2', group: 'SBP' },
  { key: 'sbp_equipements', label: 'SBP \u00C9quipements', unit: 'm\u00B2', group: 'SBP' },
  { key: 'sbp_total', label: 'SBP Total', unit: 'm\u00B2', group: 'SBP' },
];

function getVal(project: GrandProjet, key: keyof GrandProjet): number {
  const v = project[key];
  return typeof v === 'number' ? v : 0;
}

function formatDelta(source: number, current: number): { text: string; className: string } | null {
  if (source === current) return null;
  const diff = current - source;
  const pct = source !== 0 ? ((diff / source) * 100).toFixed(1) : null;
  const sign = diff > 0 ? '+' : '';
  const text = pct ? `${sign}${fmt(diff)} (${sign}${pct}%)` : `${sign}${fmt(diff)}`;
  return { text, className: diff > 0 ? (styles.deltaUp ?? '') : (styles.deltaDown ?? '') };
}

export function ScenarioComparisonPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [source, setSource] = useState<GrandProjet | null>(null);
  const [forks, setForks] = useState<GrandProjet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForkDialog, setShowForkDialog] = useState(false);
  const [forkName, setForkName] = useState('');
  const [forking, setForking] = useState(false);

  const loadData = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const [proj, projForks] = await Promise.all([
        getProject(projectId),
        listProjectForks(projectId),
      ]);
      setSource(proj);
      setForks(projForks);
    } catch {
      toast.error('Failed to load project scenarios.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleFork() {
    if (!projectId) return;
    try {
      setForking(true);
      const name = forkName.trim() || undefined;
      const forked = await forkProject(projectId, name);
      toast.success(`Scenario "${forked.name}" created.`);
      setShowForkDialog(false);
      setForkName('');
      // Reload forks
      const updatedForks = await listProjectForks(projectId);
      setForks(updatedForks);
    } catch {
      toast.error('Failed to create scenario.');
    } finally {
      setForking(false);
    }
  }

  // All scenarios = source + forks
  const scenarios = useMemo(() => {
    if (!source) return [];
    return [source, ...forks];
  }, [source, forks]);

  // Group metrics
  const metricGroups = useMemo(() => {
    const groups: Record<string, MetricDef[]> = {};
    for (const m of METRICS) {
      if (!groups[m.group]) groups[m.group] = [];
      groups[m.group]!.push(m);
    }
    return groups;
  }, []);

  // Bar chart data
  const chartData = useMemo(() => {
    if (scenarios.length < 2) return [];
    return [
      { name: 'Logements', ...Object.fromEntries(scenarios.map((s) => [s.name, s.logements])) },
      { name: 'Emplois', ...Object.fromEntries(scenarios.map((s) => [s.name, s.emplois])) },
      { name: 'SBP Total', ...Object.fromEntries(scenarios.map((s) => [s.name, s.sbp_total])) },
    ];
  }, [scenarios]);

  const chartMetrics = useMemo(
    () => scenarios.map((s, i) => ({
      key: s.name,
      label: s.name.length > 25 ? s.name.slice(0, 25) + '...' : s.name,
      color: SCENARIO_COLORS[i % SCENARIO_COLORS.length]!,
    })),
    [scenarios],
  );

  if (loading) return <div className={styles.loading}>Loading scenarios...</div>;
  if (!source) return <div className={styles.loading}>Project not found.</div>;

  const colClass = scenarios.length <= 2
    ? styles.theater2
    : scenarios.length === 3
      ? styles.theater3
      : styles.theater4;

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <h2>Scenario Comparison</h2>
        <div className={styles.headerActions}>
          <button className={styles.backBtn} onClick={() => navigate(`/app/projects/${projectId}`)}>
            Back to Project
          </button>
          <button className={styles.forkBtn} onClick={() => setShowForkDialog(true)}>
            + New Scenario
          </button>
        </div>
      </div>

      {/* Source info */}
      <div className={styles.sourceInfo}>
        <span className={styles.sourceLabel}>Base project</span>
        <span className={styles.sourceName}>{source.name}</span>
        <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
          {forks.length} scenario{forks.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* KPI summary across all scenarios */}
      {scenarios.length >= 2 && (
        <div className={styles.kpiRow}>
          {(['logements', 'emplois', 'sbp_total', 'ha_total'] as const).map((key) => {
            const label = key === 'logements' ? 'Logements' : key === 'emplois' ? 'Emplois'
              : key === 'sbp_total' ? 'SBP Total' : 'Area (ha)';
            return (
              <div key={key} className={styles.kpiCard}>
                <div className={styles.kpiLabel}>{label}</div>
                <div className={styles.kpiValues}>
                  {scenarios.map((s, i) => (
                    <span key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <span
                        className={styles.kpiDot}
                        style={{ background: SCENARIO_COLORS[i % SCENARIO_COLORS.length] }}
                      />
                      {fmt(getVal(s, key))}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Theater: side-by-side scenario columns */}
      {scenarios.length >= 2 ? (
        <div className={`${styles.theater} ${colClass}`}>
          {scenarios.map((scenario, idx) => {
            const isSource = idx === 0;
            return (
              <div
                key={scenario.id}
                className={`${styles.scenarioCol} ${isSource ? styles.scenarioColSource : ''}`}
              >
                <div className={styles.scenarioHeader}>
                  <div>
                    <div className={styles.scenarioName}>{scenario.name}</div>
                    {isSource ? (
                      <span className={styles.scenarioSourceBadge}>Base</span>
                    ) : (
                      <span className={styles.scenarioForkBadge}>Scenario</span>
                    )}
                  </div>
                  <div className={styles.scenarioActions}>
                    <button
                      className={styles.scenarioActionBtn}
                      onClick={() => navigate(`/app/projects/${scenario.id}`)}
                    >
                      Open
                    </button>
                  </div>
                </div>

                {Object.entries(metricGroups).map(([group, metrics]) => (
                  <div key={group} className={styles.metricGroup}>
                    <div className={styles.metricGroupLabel}>{group}</div>
                    {metrics.map((m) => {
                      const val = getVal(scenario, m.key);
                      const delta = !isSource ? formatDelta(getVal(source, m.key), val) : null;
                      return (
                        <div key={m.key as string} className={styles.metricRow}>
                          <span className={styles.metricLabel}>{m.label}</span>
                          <span>
                            <span className={styles.metricValue}>
                              {fmt(val)}
                              {m.unit ? ` ${m.unit}` : ''}
                            </span>
                            {delta && (
                              <span className={`${styles.metricDelta} ${delta.className}`}>
                                {delta.text}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.empty}>
          <p>No scenarios yet.</p>
          <p className={styles.emptyHint}>
            Create a scenario to explore different parameter variations
            and compare them side-by-side.
          </p>
        </div>
      )}

      {/* Visual comparison chart */}
      {chartData.length > 0 && (
        <div className={styles.chartSection}>
          <h3 className={styles.sectionTitle}>Visual Comparison</h3>
          <div className={styles.chartWrapper}>
            <ComparisonBarChart
              data={chartData}
              metrics={chartMetrics}
              layout="vertical"
            />
          </div>
        </div>
      )}

      {/* Fork dialog */}
      {showForkDialog && (
        <div className={styles.dialog} onClick={() => setShowForkDialog(false)}>
          <div className={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.dialogTitle}>Create New Scenario</h3>
            <input
              className={styles.dialogInput}
              placeholder={`${source.name} (Scenario)`}
              value={forkName}
              onChange={(e) => setForkName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleFork();
              }}
            />
            <div className={styles.dialogActions}>
              <button className={styles.dialogCancel} onClick={() => setShowForkDialog(false)}>
                Cancel
              </button>
              <button
                className={styles.dialogConfirm}
                onClick={handleFork}
                disabled={forking}
              >
                {forking ? 'Creating...' : 'Create Scenario'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
